import { Platform } from 'react-native';
import { HIDE_LOADING_MODAL, SHOW_AUTH_MODAL, SHOW_LOADING_MODAL, SHOW_OTP, SHOW_TC_MODAL, SUCCESS, INITIALISE_MENU, TOGGLE_EASY_MODE, START_ONBOARDING, FORCE_FOCUS_MODE, TOGGLE_FOCUS_MODE } from '@reduxLocal/actions/types';
import getTCConfirm from '@components/modals/TCModal/utils/getTCConfirm';
import apis from '@api/api';
import DeviceInfo from 'react-native-device-info';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { store } from '@reduxLocal/persistState';

/**
 * Common authentication utilities shared between different OAuth providers
 */

// Centralized error handler
export function handleError(dispatch: any, message: string) {
  console.error(message);
  setTimeout(() => dispatch({
    type: SHOW_AUTH_MODAL,
    data: { content: message, type: 'error' }
  }), 1000);
}

// Helper function to process user data and handle notification tokens
export async function handleUserData(res: any, notificationToken: string) {
  let amendedData = res.data.data;

  if (Platform.OS !== 'web') {
    let activeTokens = amendedData.notification_info?.map((item: any) => item.notification_token) || [];

    if (!activeTokens.length || activeTokens.includes(notificationToken)) {
      await apis.updateNotificationToken(notificationToken, amendedData.userData.id);
    }

    amendedData.expireAt = Date.now() + 1000 * 60 * 60 * 24 * 7;
  }
  return amendedData;
}

// Helper function to handle Terms & Conditions
export async function handleTermsAndConditions(res: any, dispatch: any) {
  if (res.data.terms) {
    dispatch({ type: HIDE_LOADING_MODAL });
    setTimeout(() => dispatch({ type: SHOW_TC_MODAL, payload: res.data.data.userData.id }), 1000);

    try {
      await getTCConfirm();
    } catch (e) {
      // Check if user explicitly declined (intentional cancel)
      const tcModalState = store.getState()?.modals?.tcModal;
      const wasExplicitlyDeclined = tcModalState?.accepted === false;

      if (wasExplicitlyDeclined) {
        console.log('[authUtils] User declined TC modal in handleTermsAndConditions, throwing cancellation error');
        // User cancelled - throw error to prevent handleOAuthResponse from continuing
        throw new Error('TC_MODAL_CANCELLED');
      }

      // Real error (timeout, etc.) - show error message and throw
      console.error('[authUtils] Error awaiting T&C confirmation', e);
      handleError(dispatch, "Could not fetch terms and conditions, please try again");
      throw e; // Re-throw to prevent handleOAuthResponse from continuing
    }
  }
}

// Common OAuth response handler for different providers
export async function handleOAuthResponse(
  res: any,
  dispatch: any,
  navigation: any,
  notificationToken: string,
  providerName: string
) {
  try {
    // MFA check and Terms & Conditions handling
    if (res?.status === 202) {
      dispatch({ type: HIDE_LOADING_MODAL });
      if (res.data.terms) {
        console.log('[authUtils] Status 202 with terms, showing TC modal for userId:', res.data.userId);
        // Clear any cancellation flags before showing modal (user is trying again)
        try {
          const cancelledData = await AsyncStorage.getItem('tc_cancelled');
          if (cancelledData) {
            const { userId: cancelledUserId, timestamp } = JSON.parse(cancelledData);
            // Clear if it's for the same user (they're trying again) or different user
            // Also clear if it's old (more than 30 seconds)
            const isOld = Date.now() - timestamp > 30000;
            if (cancelledUserId === res.data.userId || cancelledUserId !== res.data.userId || isOld) {
              await AsyncStorage.removeItem('tc_cancelled');
              console.log('[authUtils] Cleared cancellation flag before showing modal:', {
                cancelledUserId,
                currentUserId: res.data.userId,
                isOld,
                reason: cancelledUserId === res.data.userId ? 'same user retry' : cancelledUserId !== res.data.userId ? 'different user' : 'old flag'
              });
            }
          }
        } catch (e) {
          console.error('[authUtils] Error checking cancellation flag before showing modal:', e);
        }

        console.log('[authUtils] Dispatching SHOW_TC_MODAL with userId:', res.data.userId);
        dispatch({ type: SHOW_TC_MODAL, payload: res.data.userId });
        try {
          await getTCConfirm();
          // Clear cancellation flag on successful acceptance
          await AsyncStorage.removeItem('tc_cancelled');
          console.log('[authUtils] Terms confirmed, cleared cancellation flag');
          dispatch({ type: SHOW_OTP, payload: res.data });
        } catch (e) {
          // Check if user explicitly declined (intentional cancel)
          const tcModalState = store.getState()?.modals?.tcModal;
          const wasExplicitlyDeclined = tcModalState?.accepted === false;

          if (wasExplicitlyDeclined) {
            console.log('[authUtils] User declined TC modal, not showing error');
            // User cancelled - state is already cleared, no need to show error
            return;
          }

          // Real error (timeout, etc.) - show error message
          console.error('[authUtils] Error in getTCConfirm:', e);
          handleError(dispatch, 'Please accept our terms of service to continue');
        }
      } else {
        dispatch({ type: SHOW_OTP, payload: res.data });
      }
    } else {
      // Check if TC was cancelled by checking AsyncStorage flag
      let tcWasCancelled = false;
      try {
        const cancelledData = await AsyncStorage.getItem('tc_cancelled');
        if (cancelledData) {
          const { userId: cancelledUserId, timestamp } = JSON.parse(cancelledData);
          // Check if cancellation was recent (within last 30 seconds) and for this user
          const isRecent = Date.now() - timestamp < 30000;
          const isSameUser = res?.data?.userId === cancelledUserId || res?.data?.data?.userData?.id === cancelledUserId;
          tcWasCancelled = isRecent && isSameUser;
          console.log('[authUtils] Checking cancellation flag:', {
            cancelledUserId,
            currentUserId: res?.data?.userId || res?.data?.data?.userData?.id,
            isRecent,
            isSameUser,
            tcWasCancelled
          });

          // Clear the flag if it's old
          if (!isRecent) {
            await AsyncStorage.removeItem('tc_cancelled');
          }
        }
      } catch (e) {
        console.error('[authUtils] Error checking cancellation flag:', e);
      }

      if (tcWasCancelled) {
        console.log('[authUtils] TC was cancelled, preventing SUCCESS dispatch and clearing flag');
        await AsyncStorage.removeItem('tc_cancelled');
        return;
      }

      // Only check user data if we have a cancellation flag - this prevents blocking new users
      // New users won't have user data in Redux, so we can't use that as an indicator
      // We rely on the cancellation flag check above instead
      console.log('[authUtils] No cancellation flag found, proceeding with SUCCESS dispatch');

      // Check if TC modal was cancelled before processing success
      const tcModalState = store.getState()?.modals?.tcModal;
      const wasCancelled = tcModalState?.userId && !tcModalState?.modalDisplayed && !tcModalState?.accepted;
      console.log('[authUtils] Checking if TC was cancelled:', {
        userId: tcModalState?.userId,
        modalDisplayed: tcModalState?.modalDisplayed,
        accepted: tcModalState?.accepted,
        wasCancelled
      });

      if (wasCancelled) {
        console.log('[authUtils] TC modal was cancelled, preventing SUCCESS dispatch');
        return;
      }

      // Track if terms were just accepted to allow navigation
      let termsJustAccepted = false;
      
      try {
        await handleTermsAndConditions(res, dispatch);
        // If handleTermsAndConditions completes without error, terms were accepted
        termsJustAccepted = true;
        // Wait a bit for modal state to update after acceptance
        await new Promise(resolve => setTimeout(resolve, 200));
      } catch (e) {
        // Check if cancellation error from handleTermsAndConditions
        if (e?.message === 'TC_MODAL_CANCELLED') {
          console.log('[authUtils] TC modal was cancelled in handleTermsAndConditions, preventing SUCCESS dispatch');
          return;
        }
        // For other errors (timeout, etc.), also prevent SUCCESS dispatch
        console.log('[authUtils] Error in handleTermsAndConditions, preventing SUCCESS dispatch:', e);
        return;
      }

      // Re-check cancellation flag after handleTermsAndConditions (safety check)
      let tcWasCancelledAfter = false;
      try {
        const cancelledDataAfter = await AsyncStorage.getItem('tc_cancelled');
        if (cancelledDataAfter) {
          const { userId: cancelledUserId, timestamp } = JSON.parse(cancelledDataAfter);
          const isRecent = Date.now() - timestamp < 30000;
          const isSameUser = res?.data?.userId === cancelledUserId || res?.data?.data?.userData?.id === cancelledUserId;
          tcWasCancelledAfter = isRecent && isSameUser;

          if (tcWasCancelledAfter) {
            console.log('[authUtils] TC was cancelled during handleTermsAndConditions, preventing SUCCESS dispatch');
            await AsyncStorage.removeItem('tc_cancelled');
            return;
          }
        }
      } catch (e) {
        console.error('[authUtils] Error checking cancellation flag after handleTermsAndConditions:', e);
      }

      // Double-check TC modal state after handleTermsAndConditions
      // But if terms were just accepted, allow proceeding
      const tcModalStateAfterTC = store.getState()?.modals?.tcModal;
      if (!termsJustAccepted && tcModalStateAfterTC?.userId && !tcModalStateAfterTC?.modalDisplayed && tcModalStateAfterTC?.accepted === false) {
        console.log('[authUtils] TC modal was declined after handleTermsAndConditions, preventing SUCCESS dispatch');
        return;
      }

      const amendedData = await handleUserData(res, notificationToken);

      // Verify amendedData has valid userData before dispatching SUCCESS
      if (!amendedData?.userData?.id) {
        console.error('[authUtils] Invalid user data in amendedData, preventing SUCCESS dispatch:', amendedData);
        handleError(dispatch, 'Authentication succeeded but user data is incomplete. Please try again.');
        return;
      }

      // Final check before dispatching SUCCESS
      // But if terms were just accepted, allow proceeding
      const tcModalStateBeforeSuccess = store.getState()?.modals?.tcModal;
      if (!termsJustAccepted && tcModalStateBeforeSuccess?.userId && !tcModalStateBeforeSuccess?.modalDisplayed && tcModalStateBeforeSuccess?.accepted === false) {
        console.log('[authUtils] TC modal was cancelled before SUCCESS, preventing dispatch');
        return;
      }

      console.log('[authUtils] Dispatching SUCCESS with user data');
      dispatch({ type: SUCCESS, data: amendedData });

      // Ensure userData exists before navigating
      if (amendedData?.userData?.id) {
        // Save session to storage for persistence
        try {
          await AsyncStorage.setItem(
            'userData',
            JSON.stringify(amendedData)
          );
        } catch (storageError) {
          console.warn('Failed to save session to storage:', storageError);
        }

        // Initialize menu for web
        if (Platform.OS === 'web') {
          dispatch({ type: INITIALISE_MENU });
        }

        // Check if user is new (hasn't completed tutorial)
        // Also check if user has any existing data indicating they've used the app beyond registration
        // This prevents navigation to EasyMode if user skipped onboarding but backend hasn't updated yet
        const tutorialModal = amendedData?.userData?.tutorials?.tutorialModal;
        const userData = amendedData?.userData;
        
        // Check for indicators that user has actually used the app (not just registered)
        // These fields are typically set after registration/initial setup
        const hasCompany = amendedData?.company || userData?.currentCompany;
        const hasIntegrations = userData?.integrations && Object.keys(userData.integrations).length > 0;
        const hasLoginHistory = userData?.login_history?.previous_logins?.length > 0;
        const hasStickyNotes = userData?.sticky_notes?.length > 0;
        const hasUserInitialised = userData?.user_initialised === true;
        
        // User has activity if they have any of these indicators
        const hasAnyUserActivity = hasCompany || hasIntegrations || hasLoginHistory || hasStickyNotes || hasUserInitialised;
        
        // If tutorialModal is explicitly true, user has completed or skipped onboarding - never redirect to EasyMode
        // User is only considered "new" if tutorialModal is explicitly false AND they have no existing activity
        // If tutorialModal is undefined/null, treat conservatively - assume user has skipped onboarding unless
        // we have strong evidence they're truly new (no activity AND this appears to be first login)
        const isNewUser = tutorialModal === false && !hasAnyUserActivity;
        
        // Check TC modal state before navigating
        // But if terms were just accepted, proceed with navigation
        const tcModalVisible = store.getState()?.modals?.tcModal?.modalDisplayed;
        console.log('[authUtils] handleOAuthResponse - TC modal state:', tcModalVisible, 'termsJustAccepted:', termsJustAccepted);

        if (tcModalVisible && !termsJustAccepted) {
          console.log('[authUtils] TC modal visible and terms not just accepted, skipping direct navigation - will be handled by useEffect');
          return;
        }

        // Navigate to dashboard directly after OAuth success
        // This ensures navigation happens reliably, especially on web
        // If terms were just accepted, proceed with navigation immediately
        if (navigation && navigation.replace) {
          // Add a small delay if terms were just accepted to ensure modal is fully closed
          const navigationDelay = termsJustAccepted ? 300 : 0;
          
          setTimeout(() => {
            if (isNewUser) {
              console.log("[authUtils] [new user], starting onboarding");
              // Navigate to EasyMode screen
              if (Platform.OS != 'web') {
                console.log('[authUtils] Navigating to EasyMode');
                navigation.navigate('EasyMode');
                // Delay tutorial modal to give users time to see the dashboard and for elements to mount
                setTimeout(() => dispatch({ type: START_ONBOARDING }), 3500);
              } else {
                // Enable EasyMode and start onboarding for new users on web
                dispatch({ type: TOGGLE_EASY_MODE });
                // Delay tutorial modal to give users time to see the dashboard and for elements to mount
                // FirstTutorial will wait for elements to be ready, but we give extra time here
                setTimeout(() => dispatch({ type: START_ONBOARDING }), 3500);
                console.log('[authUtils] Navigating to Dashboard/EasyMode');
                navigation.replace('Dashboard', {
                  screen: 'EasyMode',
                  params: { session: amendedData },
                });
              }
            } else {
              console.log('[authUtils] Navigating to Dashboard/Welcome');
              navigation.replace('Dashboard', {
                screen: 'Welcome',
                params: { session: amendedData },
              });
            }
          }, navigationDelay);
        } else {
          // Fallback: Navigation will be handled by useEffect in LoginScreen
          // This prevents errors if navigation is not available
          console.log('[authUtils] Navigation not available, relying on useEffect in LoginScreen');
        }
      } else {
        console.error('OAuth response missing userData.id:', amendedData);
        handleError(dispatch, 'Authentication succeeded but user data is incomplete. Please try again.');
      }
    }
  } catch (e) {
    handleError(dispatch, `Failed to authenticate ${providerName} Account, please try again`);
  } finally {
    dispatch({ type: HIDE_LOADING_MODAL });
  }
}

// Common user type interface
export interface OAuthUser {
  surname?: string;
  name?: string;
  id: string;
  email: string;
  oauthToken: string;
  type: string;
  notificationToken: string;
  device: {
    brand: string;
    model: string;
    os: string;
    osVersion: string;
  };
}

// Create OAuth user object
export function createOAuthUser(
  user: any,
  token: string,
  type: string,
  notificationToken: string
): OAuthUser {
  return {
    surname: user.family_name || user.surname,
    name: user.given_name || user.name,
    id: user.id,
    email: user.email,
    oauthToken: token,
    type,
    notificationToken,
    device: {
      brand: DeviceInfo.getBrand(),
      model: DeviceInfo.getModel(),
      os: DeviceInfo.getSystemName(),
      osVersion: DeviceInfo.getSystemVersion(),
    },
  };
}

// Common function to handle OAuth login flow
export async function handleOAuthLogin(
  user: OAuthUser,
  dispatch: any,
  navigation: any,
  notificationToken: string,
  providerName: string
) {
  try {
    dispatch({ type: SHOW_LOADING_MODAL });

    const res = await apis.handleOAuth(user);
    await handleOAuthResponse(res, dispatch, navigation, notificationToken, providerName);

  } catch (e) {
    handleError(dispatch, `Failed to authenticate ${providerName} Account, please try again`);
  }
}

// Common function to fetch user info from OAuth provider
export async function fetchUserInfoFromProvider(
  apiUrl: string,
  token: string,
  setUserInfo: (userInfo: any) => void,
  providerName: string
) {
  try {
    const apiResponse = await fetch(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    console.log(`${providerName} API Response:`, apiResponse);

    if (apiResponse.ok) {
      const user = await apiResponse.json();
      setUserInfo(user);
      console.log(`${providerName} User Info:`, user);
      return user;
    } else {
      throw new Error(`Failed to fetch user information from ${providerName}`);
    }
  } catch (error) {
    console.error(`Failed to fetch ${providerName} user info:`, error);
    throw error;
  }
}
